import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  ConfigProvider,
  DatePicker,
  Input,
  Pagination,
  Select,
  Tag,
  Tooltip,
} from "antd";
import datePickerLocale from "antd/lib/locale/pt_BR";
import { groupBy } from "lodash";
import moment from "moment";
import {
  FaFilter,
  FaCaretDown,
  FaFlagCheckered,
  FaFlag,
  FaHandPaper,
  FaPlus,
  FaRegCalendarAlt,
  FaArrowCircleLeft,
} from "react-icons/fa";
import { MdOutlineChat } from "react-icons/md";
import { RiTimerLine } from "react-icons/ri";

import AnimatedError from "../../../components/AnimatedError";
import Can from "../../../components/Can";
import LoadingElevaty from "../../../components/LoadingElevaty";
import { NewTimelineItemModal } from "../../../components/Timeline/NewTimelineItemModal";
import { useClient } from "../../../hooks/ClientProvider";
import api from "../../../services/api";
import { SolicitationService } from "../../../services/timelineService";
import {
  formatHorizonDateTimeDash,
  getFormatCalendar,
  toCalendarDayParam,
} from "../../../utils/formatDate";
import {
  formatCustomDate,
  getChangeIn,
  getChangeTo,
  getNewTimeLineV2FullDateAndTime,
  getNewTimeLineV2Hours,
  getObs,
  getTimelineDescription,
} from "../../../utils/getTimeline";
import { notifyError } from "../../../utils/notifications";
import * as Style from "./style";
import designSystemColors from "../../../styles";

const { RangePicker } = DatePicker;

const { Option } = Select;

const FILTER_RANGES = {
  SAC: "SAC",
  OCCURRENCES: "OCCURRENCES",
};

export function NewTimelineV2() {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [lastYear, setLastYear] = useState(() => {
    const FinishDate = new Date();
    FinishDate.setMonth(FinishDate.getMonth() + 1);
    FinishDate.setDate(0);
    FinishDate.setMonth(FinishDate.getMonth() - 6);
    return FinishDate;
  });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [today, setToday] = useState(() => {
    const InitialDate = new Date();
    InitialDate.setMonth(InitialDate.getMonth() + 1);
    InitialDate.setDate(0);
    return InitialDate;
  });
  const [filterRangeActive, setFilterRangeActive] = useState(FILTER_RANGES.SAC);

  const [protocolFilterType, setProtocolFilterType] = useState("protocol");
  const [value, setValue] = useState();
  const MAX_LENGTH = 90;

  const { client } = useClient();

  const [timelineHistory, setTimelineHistory] = useState({});
  const [page, setPage] = useState(1);
  const [desc, setDesc] = useState(null);
  const [protocol, setProtocol] = useState(null);
  const [limit, setLimit] = useState(10);

  const [itemSelected, setItemSelected] = useState(null);
  const [occurrence, setOccurrence] = useState(null);

  const [loading, setLoading] = useState(false);

  const [endDate, setEndDate] = useState(() => {
    const FinishDate = new Date();
    FinishDate.setMonth(FinishDate.getMonth() + 1);
    FinishDate.setDate(0);
    FinishDate.setMonth(FinishDate.getMonth() - 6);
    return FinishDate;
  });
  const [startDate, setStartDate] = useState(() => {
    const InitialDate = new Date();
    InitialDate.setMonth(InitialDate.getMonth() + 1);
    InitialDate.setDate(0);
    return InitialDate;
  });

  const [isNewTimelineItemModalOpen, setIsNewTimelineItemModalOpen] =
    useState(false);

  const callEndDate = () => {
    const FinishDate = new Date();
    FinishDate.setMonth(FinishDate.getMonth() + 1);
    FinishDate.setDate(0);
    FinishDate.setMonth(FinishDate.getMonth() - 6);
    setEndDate(FinishDate);
  };

  const callInitDate = () => {
    const InitialDate = new Date();
    InitialDate.setMonth(InitialDate.getMonth() + 1);
    InitialDate.setDate(0);
    setStartDate(InitialDate);
  };

  const callEndDateOccurence = () => {
    const dayStr = getFormatCalendar(new Date());
    setEndDate(new Date(`${dayStr}T00:00:00`).toISOString());
  };

  const callInitDateOccurence = () => {
    const InitialDate = new Date();
    InitialDate.setMonth(InitialDate.getMonth() - 2);
    const dayStr = getFormatCalendar(InitialDate);
    setStartDate(new Date(`${dayStr}T00:00:00`).toISOString());
  };

  const fetchOccurrences = useCallback(
    async (InitialDate, FinishDate, desc, protocol) => {
      setItemSelected(null);

      try {
        setLoading(true);

        const response = await api.get("occurrence_service", {
          client_id: client.client.id,
          dateStart: toCalendarDayParam(InitialDate),
          dateEnd: toCalendarDayParam(FinishDate),
          desc,
          protocol,
        });

        if (!response?.data?.content[0]) {
          notifyError("Nenhum registro encontrado");
          return;
        }

        setOccurrence(response?.data?.content[0]);

        setTimelineHistory(response);
      } catch (err) {
        notifyError(
          "Erro",
          "Período entre datas informadas superior a 90 dias."
        );
        console.error("Erro ao listar as ocorrências", err.message);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const onChangeTodayRangeDate = useCallback(
    (date) => {
      if (filterRangeActive === FILTER_RANGES.OCCURRENCES) {
        setStartDate(date[0].toDate().toISOString());
        setEndDate(date[1].toDate().toISOString());
      } else {
        setEndDate(date[0].toDate().toISOString());
        setStartDate(date[1].toDate().toISOString());
      }
    },
    [filterRangeActive]
  );

  const fetchItems = useCallback(
    async (startDate, endDate, page, limit, protocol, desc) => {
      setLoading(true);
      try {
        const response = await SolicitationService.list({
          processorClientId: client.client.id,
          protocol,
          desc,
          page,
          limit,
          startDate,
          endDate,
        });

        setTimelineHistory(response);
      } catch (e) {
        notifyError("Falha ao buscar timeline de atendimento do cliente");
      } finally {
        setLoading(false);
      }
    },
    [startDate, endDate]
  );

  const groupedByTicketProtocol = useMemo(
    () => groupBy(timelineHistory.data, "ticket.protocol"),
    [timelineHistory]
  );

  // eslint-disable-next-line consistent-return
  const keys = useMemo(() => {
    if (groupedByTicketProtocol) {
      const List = Object.values(groupedByTicketProtocol)
        .sort((a, b) => {
          const createdAtA = new Date(a[0]?.created_at);
          const createdAtB = new Date(b[0]?.created_at);
          return createdAtB - createdAtA;
        })
        .map((item) => item[0]?.ticket?.protocol);

      setItemSelected({
        id: List[0],
        infos: groupedByTicketProtocol[List[0]],
      });

      return List;
    }
  }, [groupedByTicketProtocol]);

  const onSubmitHandler = useCallback(
    (e) => {
      e.preventDefault();

      const handlers = {
        protocol: setProtocol,
        desc: setDesc,
      };

      const setter = handlers[protocolFilterType];

      setter(value);
      setPage(1);
      setValue(null);
    },
    [value]
  );

  const getInfosTables = useCallback(() => {
    setTimelineHistory({});
    if (filterRangeActive === FILTER_RANGES.OCCURRENCES) {
      fetchOccurrences(startDate, endDate, desc, protocol);
    } else {
      fetchItems(startDate, endDate, page, limit, protocol, desc);
    }
  }, [filterRangeActive, startDate, endDate, page, limit, protocol, desc]);

  useEffect(() => {
    getInfosTables();
  }, [
    client,
    page,
    limit,
    protocol,
    desc,
    startDate,
    endDate,
    filterRangeActive,
  ]);

  useEffect(() => {
    callInitDate();
    callEndDate();
  }, []);

  const contentTimeline = useCallback(() => {
    if (itemSelected && itemSelected.infos && itemSelected.infos.length > 0) {
      const createdAtFirst = new Date(
        itemSelected.infos[itemSelected.infos.length - 1].created_at
      );
      const createdAtLast = new Date(itemSelected.infos[0].created_at);
      const timeDifferenceMilliseconds = createdAtLast - createdAtFirst;

      const totalSeconds = Math.floor(timeDifferenceMilliseconds / 1000);
      const days = Math.floor(totalSeconds / (24 * 3600));

      const remainingSecondsAfterDays = totalSeconds % (24 * 3600);
      const hours = Math.floor(remainingSecondsAfterDays / 3600);

      const remainingSecondsAfterHours = remainingSecondsAfterDays % 3600;
      const minutes = Math.floor(remainingSecondsAfterHours / 60);

      const seconds = remainingSecondsAfterHours % 60;

      let message = "";
      if (days > 0) {
        message += `${days} dia${days > 1 ? "s" : ""} `;
      }
      if (hours > 0) {
        message += `${hours} hora${hours > 1 ? "s" : ""} `;
      }
      if (minutes > 0) {
        message += `${minutes} minuto${minutes > 1 ? "s" : ""} `;
      }
      if (seconds > 0) {
        message += `${seconds} segundo${seconds > 1 ? "s" : ""}`;
      }
      if (!message) {
        message = "Em atendimento";
      }

      return (
        <>
          {message ? (
            <div className="timeline__call-duration">
              <div className="timeline__call-duration--title">
                <p>Duração do chamado</p>
              </div>
              <div className="timeline__call-duration--icons">
                <div className="info-icon">
                  <RiTimerLine />
                </div>
                <p> {message}</p>
              </div>
            </div>
          ) : null}

          <div className="timeline-time">
            {itemSelected.infos.length > 1 ? (
              <>
                {itemSelected.infos[itemSelected.infos.length - 1] ? (
                  <p>
                    <FaFlag /> Inicio às{" "}
                    {getNewTimeLineV2Hours(
                      itemSelected.infos[itemSelected.infos.length - 1]
                    )}
                  </p>
                ) : null}

                {itemSelected.infos[0] ? (
                  <p>
                    <FaFlagCheckered /> Termino às{" "}
                    {getNewTimeLineV2Hours(itemSelected.infos[0])}
                  </p>
                ) : null}
              </>
            ) : (
              <>
                {itemSelected.infos[0] ? (
                  <p>
                    <FaFlag /> Inicio às{" "}
                    {getNewTimeLineV2Hours(itemSelected.infos[0])}
                  </p>
                ) : null}
              </>
            )}
          </div>

          {itemSelected.infos &&
            itemSelected.infos.map((item) => (
              <>
                {item.desc ? (
                  <div className="generated-requests">
                    <div className="generated-requests__header">
                      <div className="generated-requests--title">
                        <p>
                          Ação realizada por:{" "}
                          <strong>{item?.user?.username}</strong>
                        </p>
                      </div>
                      <div className="generated-requests--date">
                        <p>
                          <FaRegCalendarAlt />{" "}
                          {getNewTimeLineV2FullDateAndTime(item)}
                        </p>
                      </div>
                    </div>

                    <div className="generated-requests--content">
                      <div className="gerenated-requests--content__header">
                        <div className="gerenated-requests--content__header--icon">
                          <FaHandPaper />
                        </div>
                        <div className="gerenated-requests--content__header--text">
                          <p>{item.desc}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}
                {item.change_in && item.change_to ? (
                  <div className="actions">
                    <div className="actions__header">
                      <div className="actions__header--title">
                        <p>{getTimelineDescription(item)}</p>
                      </div>
                      <div className="actions__header--date">
                        <p>
                          <FaRegCalendarAlt />{" "}
                          {getNewTimeLineV2FullDateAndTime(item)}
                        </p>
                      </div>
                    </div>

                    <div className="actions__content">
                      <div className="actions__content--action">
                        <p>
                          <FaArrowCircleLeft /> {getChangeIn(item)}
                        </p>
                      </div>
                      <div className="actions__content--action reverse">
                        <p>
                          <FaArrowCircleLeft /> {getChangeTo(item)}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : null}

                {item.obs ? (
                  <div className="comments">
                    <div className="comments__header">
                      <div className="comments__header--title">
                        <p>Comentário</p>
                      </div>
                      <div className="comments__header--date">
                        <p>
                          <FaRegCalendarAlt />{" "}
                          {getNewTimeLineV2FullDateAndTime(item)}
                        </p>
                      </div>
                    </div>

                    <div className="comments__content">
                      <div className="comments__content--icon">
                        <MdOutlineChat />
                      </div>
                      <div className="comments__content--message">
                        <p>{getObs(item)}</p>
                      </div>
                    </div>
                  </div>
                ) : null}
              </>
            ))}
        </>
      );
    }
    return null;
  }, [itemSelected]);

  const contentOcurrence = () => {
    if (occurrence) {
      return (
        <>
          <div className="generated-requests">
            <div className="generated-requests--content">
              <div className="gerenated-requests--content__header">
                <div className="gerenated-requests--content__header--icon">
                  <FaHandPaper />
                </div>
                <div className="gerenated-requests--content__header--text">
                  <p>{occurrence?.descricaoOcorrencia}</p>
                </div>
              </div>
            </div>
          </div>
        </>
      );
    }
    return null;
  };

  const contentChangeViewTable = useCallback(() => {
    return (
      <Style.ActionsBTNSTimelineHistory>
        <div className="actions-grid">
          <button
            type="button"
            className={filterRangeActive === FILTER_RANGES.SAC ? "active" : ""}
            onClick={() => {
              setValue(null);
              setProtocol(null);
              setDesc(null);
              setPage(1);
              setProtocolFilterType("protocol");
              setFilterRangeActive(FILTER_RANGES.SAC);
              callInitDate();
              callEndDate();
            }}
          >
            SAC
          </button>
          <button
            type="button"
            className={
              filterRangeActive === FILTER_RANGES.OCCURRENCES ? "active" : ""
            }
            onClick={() => {
              setValue(null);
              setProtocol(null);
              setDesc(null);
              setPage(1);
              setProtocolFilterType("protocol");
              setFilterRangeActive(FILTER_RANGES.OCCURRENCES);
              callInitDateOccurence();
              callEndDateOccurence();
            }}
          >
            Ocorrências
          </button>
        </div>
      </Style.ActionsBTNSTimelineHistory>
    );
  }, [filterRangeActive]);

  const FilterDate = useCallback(() => {
    if (startDate && endDate) {
      return (
        <ConfigProvider locale={datePickerLocale}>
          {filterRangeActive === FILTER_RANGES.OCCURRENCES ? (
            <Tooltip title="Limite máximo de 90 dias para consulta.">
              <RangePicker
                value={[moment(startDate), moment(endDate)]}
                format="DD/MM/YYYY"
                onChange={onChangeTodayRangeDate}
              />
            </Tooltip>
          ) : (
            <RangePicker
              value={[moment(startDate), moment(endDate)]}
              format="DD/MM/YYYY"
              onChange={onChangeTodayRangeDate}
            />
          )}
        </ConfigProvider>
      );
    }
    return null;
  }, [startDate, endDate]);

  const onPaginationChangeHandler = useCallback((nextPage, pageSize) => {
    setPage(nextPage);
    setLimit(pageSize);
  }, []);

  const handleNewTimelineClick = useCallback(() => {
    setIsNewTimelineItemModalOpen(true);
  }, []);

  const handleCloseNewTimelineItemModal = useCallback(() => {
    setIsNewTimelineItemModalOpen(false);
  }, []);

  const onCreateNewTimelineItem = useCallback(() => {
    setTimelineHistory({});
    fetchItems(startDate, endDate, page, limit, protocol, desc);
    handleCloseNewTimelineItemModal();
  }, [
    fetchItems,
    startDate,
    endDate,
    page,
    limit,
    protocol,
    desc,
    handleCloseNewTimelineItemModal,
  ]);

  return (
    <>
      <Can permission="view_timeline">
        <Style.ContainerTimeLineV2>
          <div className="timeline-v2__header">
            <div className="timeline-v2__header--left">
              <h2>Histórico de atendimento</h2>
            </div>
            <div className="timeline-v2__header--right">
              <div className="timeline-v2__header--range">{FilterDate()}</div>

              <div className="timeline-v2__header--filter">
                <span>
                  <Select
                    value={protocolFilterType}
                    className="select-filter"
                    onChange={setProtocolFilterType}
                  >
                    <Option value="protocol">Protocolo</Option>
                    <Option value="desc">Descrição</Option>
                  </Select>
                  <Input
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                  />
                </span>
                <Style.FilterButton
                  type="submit"
                  onClick={onSubmitHandler}
                  disabled={value === null}
                >
                  <FaFilter /> Filtrar
                </Style.FilterButton>
                <Can permission="permission_create_item_in_timeline">
                  <Style.NewTimelineButton
                    type="button"
                    onClick={handleNewTimelineClick}
                  >
                    <FaPlus size={16} />
                    Novo
                  </Style.NewTimelineButton>
                </Can>
              </div>
            </div>
          </div>

          <div className="timeline-v2__content">
            <Style.CardTimeLineHistory>
              {loading ? (
                <Style.LoadingTimeliveV2>
                  <LoadingElevaty />
                </Style.LoadingTimeliveV2>
              ) : (
                <>
                  <div className="timeline-v2__content--filters">
                    {desc && (
                      <Tag
                        color="blue"
                        closable
                        onClose={() => {
                          setValue(null);
                          setDesc(null);
                          setPage(1);
                        }}
                      >
                        Descrição: {desc}
                      </Tag>
                    )}
                    {protocol && (
                      <Tag
                        color="green"
                        closable
                        onClose={() => {
                          setValue(null);
                          setProtocol(null);
                          setPage(1);
                        }}
                      >
                        Protocolo: {protocol}
                      </Tag>
                    )}
                  </div>
                  {keys?.length > 0 ? (
                    <>
                      <div className="timeline-v2__content__left">
                        {contentChangeViewTable()}

                        {filterRangeActive === FILTER_RANGES.SAC ? (
                          <>
                            {timelineHistory &&
                              keys?.map((key, index) => (
                                <>
                                  <div
                                    className={`card-timeline-history__item ${
                                      itemSelected?.id === key ? "active" : ""
                                    }`}
                                    onClick={() => {
                                      setItemSelected({
                                        id: key,
                                        infos: groupedByTicketProtocol[key],
                                      });
                                    }}
                                  >
                                    <div className="card-timeline-history__date">
                                      <p>
                                        {formatCustomDate(
                                          groupedByTicketProtocol[key][0]
                                            ?.created_at
                                        )}
                                      </p>
                                    </div>
                                    <div className="card-timeline-history__title">
                                      <div className="card-timeline-history__title--left">
                                        <div className="title">
                                          <p>
                                            {
                                              groupedByTicketProtocol[key][0]
                                                ?.desc
                                            }
                                          </p>
                                        </div>
                                        <div className="protol">
                                          <p>Prot. #{key}</p>
                                        </div>

                                        <div className="user">
                                          <p>
                                            <strong>Última ação: </strong>
                                            <strong
                                              style={{
                                                color:
                                                  designSystemColors.textDark,
                                              }}
                                            >
                                              {groupedByTicketProtocol[key][0]
                                                ?.user?.username ??
                                                ` ${groupedByTicketProtocol[key][0]?.user?.username}`}
                                            </strong>
                                          </p>
                                        </div>
                                      </div>

                                      <div className="card-timeline-history__title--right">
                                        <div className="arrow">
                                          <FaCaretDown />
                                        </div>
                                      </div>
                                    </div>
                                    <div className="card-timeline-history__content mobile">
                                      {contentTimeline()}
                                    </div>
                                  </div>
                                </>
                              ))}
                          </>
                        ) : (
                          <>
                            {timelineHistory &&
                              timelineHistory?.data?.content && (
                                <>
                                  {timelineHistory?.data?.content.map((key) => (
                                    <>
                                      <div
                                        className={`card-timeline-history__item ${
                                          occurrence?.idOcorrencia ===
                                          key?.idOcorrencia
                                            ? "active"
                                            : ""
                                        }`}
                                        onClick={() => {
                                          setOccurrence(key);
                                        }}
                                      >
                                        <div className="card-timeline-history__date">
                                          <p>
                                            {formatHorizonDateTimeDash(key?.dataHora)}
                                          </p>
                                        </div>
                                        <div className="card-timeline-history__title">
                                          <div className="card-timeline-history__title--left">
                                            <div className="title">
                                              <p>
                                                {key?.descricaoOcorrencia
                                                  .length > MAX_LENGTH
                                                  ? `${key?.descricaoOcorrencia?.substring(
                                                      0,
                                                      MAX_LENGTH
                                                    )} ...`
                                                  : key?.descricaoOcorrencia}
                                              </p>
                                            </div>
                                            <div className="protol">
                                              <p>Prot. #{key?.idOcorrencia}</p>
                                            </div>

                                            <div className="user">
                                              <p>
                                                {key?.nomeUsuario
                                                  ? `${key?.nomeUsuario}`
                                                  : "-"}
                                              </p>
                                            </div>
                                          </div>

                                          <div className="card-timeline-history__title--right">
                                            <div className="arrow">
                                              <FaCaretDown />
                                            </div>
                                          </div>
                                        </div>
                                        <div className="card-timeline-history__content mobile">
                                          {contentOcurrence()}
                                        </div>
                                      </div>
                                    </>
                                  ))}
                                </>
                              )}
                          </>
                        )}
                      </div>

                      <div className="timeline-v2__content__right">
                        <div className="card-timeline-history__content desktop">
                          {filterRangeActive === FILTER_RANGES.SAC ? (
                            <>{contentTimeline()}</>
                          ) : (
                            <>{contentOcurrence()}</>
                          )}
                        </div>
                      </div>
                      <Style.PaginationTimeliveV2>
                        <Pagination
                          current={page}
                          onChange={onPaginationChangeHandler}
                          total={timelineHistory?.total ?? 1}
                          showSizeChanger={false}
                        />
                      </Style.PaginationTimeliveV2>
                    </>
                  ) : (
                    <div className="timeline-v2__content--empty">
                      {contentChangeViewTable()}
                      <AnimatedError width={120} height={120} />
                      <p>Nenhum registro encontrado</p>
                    </div>
                  )}
                </>
              )}
            </Style.CardTimeLineHistory>
          </div>
        </Style.ContainerTimeLineV2>
      </Can>
      <NewTimelineItemModal
        isOpen={isNewTimelineItemModalOpen}
        onClose={handleCloseNewTimelineItemModal}
        clientId={client.client.id}
        onSuccess={onCreateNewTimelineItem}
      />
    </>
  );
}
